import React, { Component } from "react";
import mapboxgl from "mapbox-gl";
import PropTypes from "prop-types";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import supercluster from "supercluster";
import bearing from "@turf/bearing";
import centroid from "@turf/centroid";
import distance from "@turf/distance";
import { point } from "@turf/helpers";
import "./Map.css";
import theme from "../../config/theme";
import {
  changeSelectedResult,
  updateDeparture,
  updateDestination,
  searchLoop
} from "../../modules/search";
import {
  handleMenuContextStyle,
  updateLastUserPosition
} from "../../modules/map";
import MenuMap from "./MenuMap/MenuMap";
import { getCheckpoints } from "../../config/api";

let shouldWaitForLayer = true;

const mapFunctionality = [
  "scrollZoom",
  "boxZoom",
  "dragRotate",
  "dragPan",
  "keyboard",
  "doubleClickZoom",
  "touchZoomRotate"
];

const maxBoundsFR = new mapboxgl.LngLatBounds(
  new mapboxgl.LngLat(-21.53, 29.84),
  new mapboxgl.LngLat(26.89, 58.72)
);

class Map extends Component {
  constructor() {
    super();
    this.state = {
      currentSource: [],
      currentPointSource: [],
      currentCoordinates: null
    };
    this.updateDimensions = this.updateDimensions.bind(this);
  }

  componentWillMount() {
    this.updateDimensions();
  }

  componentDidMount() {
    mapboxgl.accessToken =
      "pk.eyJ1IjoibWFraW5hY29ycHVzIiwiYSI6ImNqY3E4ZTNwcTFta3ozMm80d2xzY29wM2MifQ.Nwl_FHrWAIQ46s_lY0KNiQ";
    const map = new mapboxgl.Map({
      container: "map",
      style: "mapbox://styles/makinacorpus/cjcq8s3pu4z6t2rotrljw6tet",
      zoom: 1,
      maxBounds: maxBoundsFR
    });

    map.addControl(new mapboxgl.NavigationControl());

    const geolocateControl = new mapboxgl.GeolocateControl({
      positionOptions: {
        enableHighAccuracy: true
      },
      trackUserLocation: true
    });

    map.addControl(geolocateControl);

    map.on("render", () => this.computeLoop());

    let timer;

    map.on("load", () => {
      geolocateControl.on("geolocate", e => {
        this.props.updateLastUserPosition([
          e.coords.longitude,
          e.coords.latitude
        ]);
      });
      geolocateControl.on("trackuserlocationstart", e => {
        if (e.target._userLocationDotMarker._lngLat) {
          this.props.updateLastUserPosition([
            e.target._userLocationDotMarker._lngLat.lng,
            e.target._userLocationDotMarker._lngLat.lat
          ]);
        }
      });
      geolocateControl.on("trackuserlocationend", e =>
        this.props.updateLastUserPosition(undefined)
      );

      map.addSource("moodwalkr", {
        type: "vector",
        url: "https://moodwalkr.com/tiles/moodwalkr.json"
      });

      map.addLayer(
        {
          id: "culture-points",
          type: "circle",
          source: "moodwalkr",
          "source-layer": "osm_culture_points",
          minzoom: 0,
          maxzoom: 24,
          filter: ["all"],
          layout: {
            visibility: "visible"
          },
          paint: {
            "circle-color": "#fbcc5d",
            "circle-opacity": 0
          }
        },
        "waterway-label"
      );

      map.addLayer(
        {
          id: "culture-polygons",
          type: "fill",
          source: "moodwalkr",
          "source-layer": "osm_culture_polygons",
          minzoom: 0,
          maxzoom: 24,
          filter: ["all"],
          layout: {
            visibility: "visible"
          },
          paint: {
            "fill-color": "#fbcc5d",
            "fill-antialias": true,
            "fill-opacity": 0
          }
        },
        "waterway-label"
      );

      map.addLayer(
        {
          id: "nightlife-points",
          type: "circle",
          source: "moodwalkr",
          "source-layer": "osm_nightlife_points",
          minzoom: 0,
          maxzoom: 24,
          filter: ["all"],
          layout: {
            visibility: "visible"
          },
          paint: {
            "circle-color": "#c2b2ea",
            "circle-opacity": 0
          }
        },
        "waterway-label"
      );

      map.addLayer(
        {
          id: "nightlife-polygons",
          type: "fill",
          source: "moodwalkr",
          "source-layer": "osm_nightlife_polygons",
          minzoom: 0,
          maxzoom: 24,
          filter: ["all"],
          layout: {
            visibility: "visible"
          },
          paint: {
            "fill-color": "#c2b2ea",
            "fill-antialias": true,
            "fill-opacity": 0
          }
        },
        "waterway-label"
      );

      map.addLayer(
        {
          id: "nightlife-points-label",
          type: "symbol",
          source: "moodwalkr",
          "source-layer": "osm_nightlife_points",
          minzoom: 14,
          maxzoom: 24,
          filter: ["all"],
          layout: {
            visibility: "visible",
            "text-field": "{name} \n {label_fr}",
            "text-font": ["Open Sans Regular"],
            "text-offset": [0, 1]
          },
          paint: {
            "text-color": "#291e27",
            "text-opacity": 0
          }
        },
        "country-label-lg"
      );

      map.addLayer(
        {
          id: "nightlife-polygons-label",
          type: "symbol",
          source: "moodwalkr",
          "source-layer": "osm_nightlife_polygons_label",
          minzoom: 14,
          maxzoom: 24,
          filter: ["all"],
          layout: {
            visibility: "visible",
            "text-field": "{name} \n {label_fr}",
            "text-font": ["Open Sans Regular"],
            "text-ignore-placement": false,
            "text-justify": "center",
            "text-optional": false,
            "symbol-avoid-edges": false,
            "text-allow-overlap": false
          },
          paint: {
            "text-color": "#291e27",
            "text-opacity": 0
          }
        },
        "country-label-lg"
      );

      map.addLayer(
        {
          id: "culture-points-label",
          type: "symbol",
          source: "moodwalkr",
          "source-layer": "osm_culture_points",
          minzoom: 14,
          maxzoom: 24,
          filter: ["all"],
          layout: {
            visibility: "visible",
            "text-field": "{name} \n {label_fr}",
            "text-font": ["Open Sans Regular"],
            "text-offset": [0, 1]
          },
          paint: {
            "text-color": "#42361a",
            "icon-opacity": 1,
            "text-opacity": 0
          }
        },
        "country-label-lg"
      );

      map.addLayer(
        {
          id: "culture-polygons-label",
          type: "symbol",
          source: "moodwalkr",
          "source-layer": "osm_culture_polygons_label",
          minzoom: 14,
          maxzoom: 24,
          filter: ["all"],
          layout: {
            visibility: "visible",
            "text-field": "{name} \n {label_fr}",
            "text-font": ["Open Sans Regular"],
            "text-ignore-placement": false,
            "text-justify": "center",
            "text-optional": false
          },
          paint: {
            "text-color": "#42361a",
            "text-opacity": 0
          }
        },
        "country-label-lg"
      );

      map.loadImage("../assets/departure-point.png", (error, image) => {
        if (error) throw error;
        map.addImage("departure", image);
      });
      map.loadImage("../assets/destination-point.png", (error, image) => {
        if (error) throw error;
        map.addImage("destination", image);
      });
    });

    map.on("contextmenu", e => this.handleMenuContext(e));

    map.on("touchstart", e => {
      if (this.props.contextMenuStyle.display === "none") {
        timer = setTimeout(r => this.handleMenuContext(e), 500);
      } else {
        this.props.handleMenuContextStyle("none", 0, 0);
      }
    });

    map.on("touchend", e => {
      if (timer) {
        clearTimeout(timer);
      }
    });

    map.on("touchmove", e => {
      if (timer) {
        clearTimeout(timer);
      }
    });

    map.on("click", () => {
      this.props.handleMenuContextStyle("none", 0, 0);
    });

    if (!this.props.isEnable) {
      map.setMaxBounds(maxBoundsFR);
      map.setZoom(1);
      mapFunctionality.map(functionality => map[functionality].disable());
    } else {
      map.setMaxBounds(
        new mapboxgl.LngLatBounds(
          new mapboxgl.LngLat(
            this.props.city.boundingbox[0][0],
            this.props.city.boundingbox[0][1]
          ),
          new mapboxgl.LngLat(
            this.props.city.boundingbox[1][0],
            this.props.city.boundingbox[1][1]
          )
        )
      );
      mapFunctionality.map(functionality => map[functionality].enable());
    }
    this.setState({ map });
    window.addEventListener("resize", this.updateDimensions);
  }

  componentWillReceiveProps(nextProps) {
    const currentSource = [];
    let currentPointSource = this.state.currentPointSource.slice();

    const featuresToBound = [];

    if (!nextProps.isEnable && this.state.map) {
      theme.forEach(t => {
        t.layers.forEach(layer => {
          const mapLayer = this.state.map.getLayer(layer);
          if (mapLayer) {
            if (mapLayer.type === "circle") {
              this.state.map.setPaintProperty(layer, "circle-opacity", 0);
            }
            if (mapLayer.type === "fill") {
              this.state.map.setPaintProperty(layer, "fill-opacity", 0);
            }
            if (mapLayer.type === "symbol") {
              this.state.map.setPaintProperty(layer, "text-opacity", 0);
            }
          }
        });
      });
    }

    if (
      nextProps.isEnable !== this.props.isEnable ||
      nextProps.city !== this.props.city
    ) {
      this.handleMapFunctionality(nextProps.isEnable, nextProps.city);
    }

    if (
      nextProps.results.length === 0 &&
      nextProps.modeTabs === "boucle" &&
      nextProps.departure &&
      nextProps.departure.coordinates &&
      nextProps.distance
    ) {
      this.state.map.jumpTo({
        center: nextProps.departure.coordinates,
        zoom: 14
      });
      shouldWaitForLayer = true;
    }

    if (nextProps.results && nextProps.results !== this.props.results) {
      this.state.currentSource.forEach(source => {
        this.state.map.removeSource(source);
        this.state.map.removeLayer(source);
        this.state.map.removeLayer(`${source}-crossing`);
      });
      if (nextProps.results.length !== 0) {
        // update source and layer
        nextProps.results.forEach(result => {
          if (result.data) {
            result.data.forEach((onePath, index) => {
              this.state.map.addSource(`${result.id}-${index}`, {
                type: "geojson",
                data: onePath.path
              });
              currentSource.push(`${result.id}-${index}`);

              this.state.map.addLayer(
                {
                  id: `${result.id}-${index}-crossing`,
                  type: "line",
                  source: `${result.id}-${index}`,
                  layout: {
                    visibility: index === 0 ? "visible" : "none",
                    "line-join": "round",
                    "line-cap": "round"
                  },
                  paint: {
                    "line-color": "#A09EB3",
                    "line-width": 5,
                    "line-dasharray": [1, 2]
                  },
                  filter: ["==", "mode", "crossing"]
                },
                "waterway-label"
              );
              this.state.map.addLayer(
                {
                  id: `${result.id}-${index}`,
                  type: "line",
                  source: `${result.id}-${index}`,
                  layout: {
                    visibility: index === 0 ? "visible" : "none",
                    "line-join": "round",
                    "line-cap": "round"
                  },
                  paint: {
                    "line-color": "#A09EB3",
                    "line-width": 5
                  },
                  filter: ["!=", "mode", "crossing"]
                },
                "waterway-label"
              );
              this.state.map.on("mouseenter", `${result.id}-${index}`, () => {
                if (this.props.mode === "inline") {
                  this.state.map.getCanvas().style.cursor = "pointer";
                }
              });
              this.state.map.on("mouseleave", `${result.id}-${index}`, () => {
                if (this.props.mode === "inline") {
                  this.state.map.getCanvas().style.cursor = "";
                }
              });
              this.state.map.on("click", `${result.id}-${index}`, () => {
                if (this.props.mode === "inline") {
                  this.props.changeSelectedResult(index);
                }
              });
              featuresToBound.push(...onePath.path.features);
            });
          }
        });
        this.fitBoundsToMap(featuresToBound);
      }

      this.setState({ currentSource });
    } else if (
      nextProps.selectedResult !== this.props.selectedResult ||
      nextProps.selectedTheme !== this.props.selectedTheme ||
      nextProps.mode !== this.props.mode
    ) {
      if (nextProps.selectedTheme.id !== this.props.selectedTheme.id) {
        if (Object.keys(this.props.selectedTheme).length > 0) {
          this.props.selectedTheme.layers.forEach(layer => {
            const mapLayer = this.state.map.getLayer(layer);
            if (mapLayer.type === "circle") {
              this.state.map.setPaintProperty(layer, "circle-opacity", 0);
            }
            if (mapLayer.type === "fill") {
              this.state.map.setPaintProperty(layer, "fill-opacity", 0);
            }
            if (mapLayer.type === "symbol") {
              this.state.map.setPaintProperty(layer, "text-opacity", 0);
            }
          });
        }
        if (Object.keys(nextProps.selectedTheme).length > 0) {
          nextProps.selectedTheme.layers.forEach(layer => {
            const mapLayer = this.state.map.getLayer(layer);
            if (mapLayer.type === "circle") {
              this.state.map.setPaintProperty(layer, "circle-opacity", 1);
            }
            if (mapLayer.type === "fill") {
              this.state.map.setPaintProperty(layer, "fill-opacity", 1);
            }
            if (mapLayer.type === "symbol") {
              this.state.map.setPaintProperty(layer, "text-opacity", 1);
            }
          });
        }
      }
      // update state or visibility for layer
      this.state.currentSource.forEach(source => {
        if (source !== "departure" && source !== "destination") {
          // const layers = [source, `${source}-crossing`];
          this.state.currentSource.forEach(layer => {
            if (layer.split("-")[0] === nextProps.selectedTheme.id) {
              if (Number(layer.split("-")[1]) === nextProps.selectedResult) {
                this.state.map.setPaintProperty(
                  layer,
                  "line-color",
                  nextProps.selectedTheme.color
                );
                this.state.map.setPaintProperty(
                  `${layer}-crossing`,
                  "line-color",
                  nextProps.selectedTheme.color
                );

                if (this.state.map.getSource(layer)) {
                  this.fitBoundsToMap(
                    this.state.map.getSource(layer)._data.features
                  );
                }
                this.state.map.moveLayer(layer, "waterway-label");
                this.state.map.moveLayer(`${layer}-crossing`, "waterway-label");
                this.state.map.moveLayer("departure");
                if (nextProps.destination.coordinates) {
                  this.state.map.moveLayer("destination");
                }
              } else {
                this.state.map.setPaintProperty(layer, "line-color", "#A09EB3");
                this.state.map.setPaintProperty(
                  `${layer}-crossing`,
                  "line-color",
                  "#A09EB3"
                );
              }
              if (nextProps.mode === "inline")
                this.state.map.setLayoutProperty(
                  layer,
                  "visibility",
                  "visible"
                );
            } else if (nextProps.mode === "inline") {
              this.state.map.setLayoutProperty(layer, "visibility", "none");
              this.state.map.setLayoutProperty(
                `${layer}-crossing`,
                "visibility",
                "none"
              );
            } else {
              this.state.map.setPaintProperty(layer, "line-color", "#A09EB3");
              this.state.map.setPaintProperty(
                `${layer}-crossing`,
                "line-color",
                "#A09EB3"
              );
            }
          });
        }
      });
    }

    if (
      (nextProps.departure &&
        nextProps.departure.coordinates !== this.props.departure.coordinates) ||
      (nextProps.destination &&
        nextProps.destination.coordinates !==
          this.props.destination.coordinates)
    ) {
      // handle departure and destination point
      if (
        nextProps.departure.coordinates &&
        nextProps.departure.coordinates !== this.props.departure.coordinates
      ) {
        if (this.state.currentPointSource.find(e => e === "departure")) {
          this.state.map.removeSource("departure");
          this.state.map.removeLayer("departure");
        } else {
          currentPointSource.push("departure");
        }
        this.state.map.addSource("departure", {
          type: "geojson",
          data: {
            type: "Feature",
            geometry: {
              type: "Point",
              coordinates: nextProps.departure.coordinates
            }
          }
        });

        this.state.map.addLayer({
          id: "departure",
          source: "departure",
          type: "symbol",
          layout: {
            "icon-image": "departure",
            "icon-size": 1
          }
        });
      } else if (
        !nextProps.departure.coordinates &&
        nextProps.departure.coordinates !== this.props.departure.coordinates
      ) {
        this.state.map.removeSource("departure");
        this.state.map.removeLayer("departure");
        currentPointSource = currentPointSource.filter(e => e !== "departure");
      }

      if (
        nextProps.destination.coordinates &&
        nextProps.destination.coordinates !== this.props.destination.coordinates
      ) {
        if (this.state.currentPointSource.find(e => e === "destination")) {
          this.state.map.removeSource("destination");
          this.state.map.removeLayer("destination");
        } else {
          currentPointSource.push("destination");
        }
        this.state.map.addSource("destination", {
          type: "geojson",
          data: {
            type: "Feature",
            geometry: {
              type: "Point",
              coordinates: nextProps.destination.coordinates
            }
          }
        });

        this.state.map.addLayer({
          id: "destination",
          source: "destination",
          type: "symbol",
          layout: {
            "icon-image": "destination",
            "icon-size": 1
          }
        });
      } else if (
        !nextProps.destination.coordinates &&
        nextProps.destination.coordinates !== this.props.destination.coordinates
      ) {
        this.state.map.removeSource("destination");
        this.state.map.removeLayer("destination");
        currentPointSource = currentPointSource.filter(
          e => e !== "destination"
        );
      }

      this.setState({ currentPointSource });
    }
    if (nextProps.destination.coordinates) {
      this.state.map.moveLayer("destination");
    }

    if (nextProps.departure.coordinates) {
      this.state.map.moveLayer("departure");
    }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.updateDimensions);
  }

  onOptionSelect = type => {
    if (type === "departure") {
      this.props.updateDeparture(null, this.state.currentCoordinates);
    } else if (type === "destination") {
      this.props.updateDestination(null, this.state.currentCoordinates);
    }
    this.props.handleMenuContextStyle("none", 0, 0);
  };

  getLoopAngle = (currentTheme, loopDistance, coordinates) => {
    const themeData = theme.find(t => t.id === currentTheme);
    const themeDataLayers = themeData.loopQueryLayers;
    const departureCoordinates = point(coordinates);
    let features = [];
    let angle;
    themeDataLayers.forEach(l => {
      const layerFeatures = this.state.map.queryRenderedFeatures({
        layers: [l.name]
      });
      if (l.type === "line" || l.type === "polygon") {
        const centroidFeatures = layerFeatures.map(feature =>
          centroid(feature)
        );
        features = [...features, ...centroidFeatures];
      } else if (l.type === "point") {
        features = [...features, ...layerFeatures];
      }
    });
    const cluster = supercluster();
    const bbox = this.state.map.getBounds();
    cluster.load(features);
    let clusters = cluster.getClusters(
      [bbox._sw.lng, bbox._sw.lat, bbox._ne.lng, bbox._ne.lat],
      Math.round(this.state.map.getZoom())
    );
    clusters = clusters
      .filter(c => c.properties.cluster)
      .sort((a, b) => b.properties.point_count - a.properties.point_count)
      .map(f => {
        f.properties.distance = distance(departureCoordinates, f);
        f.properties.bearing = bearing(departureCoordinates, f);
        return f;
      })
      .filter(c => c.properties.distance <= loopDistance);
    if (clusters[0]) {
      angle = bearing(departureCoordinates, clusters[0]);
    }
    return angle;
  };

  getCheckpoints(
    departureCoordinates,
    loopDistance,
    currentDistance,
    clusters,
    clusterI,
    checkpoints
  ) {
    if (currentDistance < loopDistance) {
      let tmpDistance = 0;
      checkpoints.push(clusters[clusterI].geometry.coordinates);
      const sortedCheckpoints = checkpoints.sort(c => {
        return bearing(departureCoordinates, c);
      });
      const points = [
        departureCoordinates,
        ...sortedCheckpoints,
        departureCoordinates
      ];

      for (let i = 0; i < points.length - 1; i++) {
        tmpDistance += distance(points[i], points[i + 1]);
      }
      return this.getCheckpoints(
        departureCoordinates,
        loopDistance,
        tmpDistance,
        clusters,
        clusterI + 1,
        checkpoints
      );
    }
    return checkpoints;
  }

  computeLoop() {
    if (this.state.map.loaded() && shouldWaitForLayer) {
      shouldWaitForLayer = false;
      if (
        this.props.results.length === 0 &&
        this.props.modeTabs === "boucle" &&
        this.props.departure &&
        this.props.departure.coordinates &&
        this.props.distance
      ) {
        const currentThemes = theme
          .filter(
            filterTheme => this.props.city.theme.indexOf(filterTheme.id) >= 0
          )
          .filter(filterTheme => filterTheme.id !== "shortest");

        const departureCoordinates = this.props.departure.coordinates;

        currentThemes.forEach(theme => {
          theme.checkpoints = getCheckpoints(
            departureCoordinates,
            this.props.distance * 0.8,
            this.getLoopAngle(
              theme.id,
              this.props.distance,
              departureCoordinates
            )
          );
        });
        this.state.map.off("render", this.computeLoop);
        this.state.map.off("render");

        this.props.searchLoop(this.props.departure.coordinates, currentThemes);
      }
    }
  }

  fitBoundsToMap(features) {
    const bounds = new mapboxgl.LngLatBounds();
    features.forEach(feature => {
      bounds.extend(
        feature.geometry.coordinates.reduce((lineBounds, coord) => {
          return lineBounds.extend(coord);
        }, new mapboxgl.LngLatBounds(feature.geometry.coordinates[0], feature.geometry.coordinates[0]))
      );
    });

    this.state.map.fitBounds(bounds, {
      padding: this.state.padding
    });
  }

  updateDimensions() {
    if (window.innerWidth > 600) {
      this.setState({
        padding: {
          top: 20,
          bottom: 20,
          left: 420,
          right: 20
        }
      });
    } else {
      this.setState({
        padding: {
          top: 20,
          bottom: 20,
          left: 20,
          right: 20
        }
      });
    }
  }

  handleMapFunctionality = (enable, city) => {
    if (this.state && this.state.map) {
      if (!enable) {
        if (!Object.keys(city).length > 0) {
          this.state.map.setMaxBounds(maxBoundsFR);
        } else {
          this.state.map.setMaxBounds(
            new mapboxgl.LngLatBounds(
              new mapboxgl.LngLat(
                city.boundingbox[0][0],
                city.boundingbox[0][1]
              ),
              new mapboxgl.LngLat(
                city.boundingbox[1][0],
                city.boundingbox[1][1]
              )
            )
          );
        }
        this.state.map.setZoom(1);
        mapFunctionality.map(functionality =>
          this.state.map[functionality].disable()
        );
      } else {
        this.state.map.setMaxBounds(
          new mapboxgl.LngLatBounds(
            new mapboxgl.LngLat(city.boundingbox[0][0], city.boundingbox[0][1]),
            new mapboxgl.LngLat(city.boundingbox[1][0], city.boundingbox[1][1])
          )
        );
        mapFunctionality.map(functionality =>
          this.state.map[functionality].enable()
        );
      }
    }
  };

  handleMenuContext = e => {
    if (this.props.isEnable) {
      const menuWidth = 231;
      const menuHeight = 112;
      const posX =
        e.point.x > window.innerWidth - menuWidth
          ? e.point.x - menuWidth
          : e.point.x;
      const posY =
        e.point.y > window.innerHeight - menuHeight
          ? e.point.y - menuHeight
          : e.point.y;
      this.props.handleMenuContextStyle("block", posY, posX);
      this.setState({ currentCoordinates: [e.lngLat.lng, e.lngLat.lat] });
    }
  };

  render() {
    return (
      <div>
        <div id="map" />
        <MenuMap
          modeTabs={this.props.modeTabs}
          onOptionSelect={this.onOptionSelect}
          contextMenuStyle={this.props.contextMenuStyle}
        />
      </div>
    );
  }
}

Map.propTypes = {
  isEnable: PropTypes.bool.isRequired,
  city: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    boundingbox: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number))
  }).isRequired,
  results: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.string,
      color: PropTypes.string,
      data: PropTypes.arrayOf(
        PropTypes.shape({
          time: PropTypes.number,
          distance: PropTypes.number,
          path: PropTypes.shape().isRequired
        })
      )
    })
  ).isRequired,
  selectedTheme: PropTypes.shape().isRequired,
  selectedResult: PropTypes.number.isRequired,
  mode: PropTypes.string.isRequired,
  changeSelectedResult: PropTypes.func.isRequired,
  handleMenuContextStyle: PropTypes.func.isRequired,
  updateDeparture: PropTypes.func.isRequired,
  updateDestination: PropTypes.func.isRequired,
  destination: PropTypes.shape().isRequired,
  departure: PropTypes.shape().isRequired,
  contextMenuStyle: PropTypes.shape().isRequired,
  modeTabs: PropTypes.string.isRequired,
  searchLoop: PropTypes.func.isRequired
};

const mapStateToProps = state => ({
  isEnable: state.map.isEnable,
  city: state.map.city,
  results: state.search.results,
  selectedResult: state.search.selectedResult,
  selectedTheme: state.search.selectedTheme,
  mode: state.search.mode,
  departure: state.search.departure,
  destination: state.search.destination,
  contextMenuStyle: state.map.contextMenuStyle,
  modeTabs: state.search.modeTabs,
  distance: state.search.distance
});

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      changeSelectedResult,
      handleMenuContextStyle,
      updateDeparture,
      updateDestination,
      searchLoop,
      updateLastUserPosition
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(Map);



// WEBPACK FOOTER //
// ./src/containers/Map/Map.jsx